import { format } from 'date-fns';

import {
  RouteActionType,
  UserRouteActionV1Model,
  getHandledJobUids,
  getJobActionType
} from '@core/models/entities/implementations/user-route-action-v1';
import {
  ROUTE_LOCK_OPTIONS,
  UserRouteV1Model
} from '@core/models/entities/implementations/user-route-v1';
import {
  CarriedItem,
  CarriedStop,
  CarriedTour,
  PlanEditGesture
} from '@core/models/plan-edit-gesture.model';
import { ICONS } from '@core/services/icons/icons.service';
import { PinnedStop } from '@core/store/plan-edit/plan-edit.state';
import { jobQuantity } from '@core/utils/plan-edit/job-quantity';
import { isPredictedActionUid } from '@core/utils/plan-edit/predictions';
import {
  actionStart,
  departureAction,
  pinOrigin
} from '@core/utils/plan-edit/route-timing';
import {
  carriedJob,
  isRouteRef,
  jobsLoadedForDeliveryOnItsRoute,
  loadsJobsDeliveredOnItsRoute
} from '@core/utils/plan-edit/targets';
import { Tour, tourMoveBlocker, toursOf } from '@core/utils/plan-edit/tours';

import { ContextMenuOption } from '@shared/components/context-menu/context-menu.component';
import { ContextMenuHeader } from '@shared/services/context-menu/context-menu.service';

import { PlanEditFormRequest } from '@features/components/plan/edit/plan-edit-forms/plan-edit-forms.service';

import { Task, TimelineRow } from '../models/timeline';

export type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The entries a stop's menu adds in edit mode; the caller decides whether the stop is editable. */
export interface StopMenuContext {
  row: TimelineRow;
  task: Task;
  pins: Record<string, PinnedStop>;
  dispatch: (gesture: PlanEditGesture) => void;
  openForm: (request: PlanEditFormRequest) => void;
  /** `name` is what the strip calls a carried tour. */
  startPlacing: (carried: CarriedItem, name?: string) => void;
  translate: Translate;
}

const MENU = 'features.plan.edit.menu';

export function stopMenuEntries(ctx: StopMenuContext): ContextMenuOption[] {
  const { row, task, translate } = ctx;
  const action = row.route?.actions.find((candidate) => candidate.uid === task.actionUid);
  if (!row.route || !action) return [];
  const routeUid = row.route.uid;

  const leaveBlocked = loadsJobsDeliveredOnItsRoute(row.route, action.uid);
  const carried: CarriedStop = {
    kind: 'stop',
    routeUid,
    actionUid: action.uid,
    siteUid: action.site_uid,
    jobUids: getHandledJobUids(action)
  };
  const blocked = {
    disabled: leaveBlocked,
    hint: leaveBlocked ? translate(`${MENU}.move_stop_loads_route`) : undefined
  };
  const entries: ContextMenuOption[] = [
    {
      icon: ICONS.arrowsOutCardinal,
      label: translate(`${MENU}.move_stop`),
      ...blocked,
      action: () => ctx.startPlacing(carried)
    },
    {
      icon: ICONS.path,
      label: translate(`${MENU}.move_stop_to`),
      ...blocked,
      action: () => ctx.openForm({ kind: 'move-to', carried, name: task.name })
    },
    ...jobMenuEntries(ctx, action),
    ...tourMenuEntries(ctx, action.uid),
    {
      icon: ICONS.clock,
      label: translate(`${MENU}.set_time`),
      action: () =>
        ctx.openForm({
          kind: 'set-time',
          routeUid,
          actionUid: action.uid,
          stopName: task.name,
          current: pinOrigin(action).toISOString(),
          // Without its site's zone the stop is timed in the browser's, which the form names.
          siteTimeZone:
            task.siteTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        })
    }
  ];
  if (ctx.pins[action.uid]) {
    entries.push({
      icon: `${ICONS.pushPin} icon-slashed`,
      label: translate(`${MENU}.release_pin`),
      action: () => ctx.dispatch({ kind: 'release-pin', routeUid, actionUid: action.uid })
    });
  }
  return entries;
}

/**
 * "Move tour" and "Move tour to…" for the tour the stop belongs to; on a one-tour route, the same
 * gesture over every stop, named "Move all stops".
 */
function tourMenuEntries(ctx: StopMenuContext, actionUid: string): ContextMenuOption[] {
  const { row, translate } = ctx;
  const tours = toursOf(row.route!);
  const tour = tours.find((candidate) => candidate.actionUids.includes(actionUid));
  if (!tour || tour.actionUids.some(isPredictedActionUid)) return [];
  const carried: CarriedTour = {
    kind: 'tour',
    routeUid: row.route!.uid,
    actionUids: tour.actionUids
  };

  if (tours.length === 1) {
    return [
      {
        icon: ICONS.stack,
        label: translate(`${MENU}.move_all_stops`),
        action: () =>
          ctx.startPlacing(
            carried,
            translate('features.plan.edit.tour.all_stops', { route: row.name })
          )
      },
      {
        icon: ICONS.path,
        label: translate(`${MENU}.move_all_stops_to`),
        action: () =>
          ctx.openForm({ kind: 'move-to', carried, name: row.name, allStops: true })
      }
    ];
  }

  const hint = tourBlockHint(tourMoveBlocker(row.route!, tour, tours), tour, translate);
  const blocked = { disabled: hint !== null, hint: hint ?? undefined };
  return [
    {
      icon: ICONS.stack,
      label: translate(`${MENU}.move_tour`),
      ...blocked,
      action: () =>
        ctx.startPlacing(
          carried,
          translate('features.plan.edit.tour.name', { index: tour.index })
        )
    },
    {
      icon: ICONS.path,
      label: translate(`${MENU}.move_tour_to`),
      ...blocked,
      action: () => ctx.openForm({ kind: 'move-to', carried, name: `${tour.index}` })
    }
  ];
}

/** Why a tour cannot move, from its `tourMoveBlocker`; `null` when nothing blocks it. */
export function tourBlockHint(
  blocker: ReturnType<typeof tourMoveBlocker>,
  tour: Tour,
  translate: Translate
): string | null {
  if (!blocker) return null;
  // A job is loaded before it is delivered: an earlier tour serving it loaded it.
  const key =
    blocker.tourIndex < tour.index ? 'move_tour_loaded_in' : 'move_tour_delivered_in';
  return translate(`${MENU}.${key}`, { job: blocker.jobUid, tour: blocker.tourIndex });
}

const JOB_KIND_KEYS: Record<RouteActionType, string> = {
  pickup: 'pickup',
  delivery: 'delivery',
  'delivery-pickup': 'delivery_pickup',
  basic: 'basic'
};

/**
 * "Move job" and "Move job to…": named after the job on a one-job stop, else each a submenu of
 * the stop's jobs.
 */
function jobMenuEntries(
  ctx: StopMenuContext,
  action: UserRouteActionV1Model
): ContextMenuOption[] {
  const { row, translate } = ctx;
  const loadedForDelivery = jobsLoadedForDeliveryOnItsRoute(row.route!, action.uid);
  const jobs = getHandledJobUids(action).map((jobUid) => {
    const blocked = loadedForDelivery.has(jobUid);
    const carried = carriedJob(row.route!, action.uid, jobUid);
    const quantity = jobQuantity(action, jobUid);
    const kind = translate(
      `${MENU}.job_kind.${JOB_KIND_KEYS[getJobActionType(action, jobUid)]}`
    );
    return {
      carried,
      meta: quantity ? `${kind} ${quantity}` : kind,
      disabled: blocked,
      hint: blocked ? translate(`${MENU}.move_job_loads_route`) : undefined
    };
  });
  const place = (job: (typeof jobs)[number]) => () => ctx.startPlacing(job.carried);
  const openForm = (job: (typeof jobs)[number]) => () =>
    ctx.openForm({ kind: 'move-to', carried: job.carried, name: job.carried.jobUid });

  if (jobs.length === 1) {
    const [job] = jobs;
    const named = { job: job.carried.jobUid };
    const state = { disabled: job.disabled, hint: job.hint };
    return [
      {
        icon: ICONS.package,
        label: translate(`${MENU}.move_job_named`, named),
        ...state,
        action: place(job)
      },
      {
        icon: ICONS.path,
        label: translate(`${MENU}.move_job_to_named`, named),
        ...state,
        action: openForm(job)
      }
    ];
  }
  if (jobs.every((job) => job.disabled)) {
    const blocked = {
      disabled: true,
      hint: translate(`${MENU}.move_stop_loads_route`),
      action: () => {}
    };
    return [
      { icon: ICONS.package, label: translate(`${MENU}.move_job`), ...blocked },
      { icon: ICONS.path, label: translate(`${MENU}.move_job_to`), ...blocked }
    ];
  }
  const submenu = (act: typeof place): ContextMenuOption[] =>
    jobs.map((job) => ({
      icon: ICONS.package,
      label: job.carried.jobUid,
      meta: job.meta,
      disabled: job.disabled,
      hint: job.hint,
      action: act(job)
    }));
  return [
    {
      icon: ICONS.package,
      label: translate(`${MENU}.move_job`),
      action: () => {},
      subOptions: submenu(place)
    },
    {
      icon: ICONS.path,
      label: translate(`${MENU}.move_job_to`),
      action: () => {},
      subOptions: submenu(openForm)
    }
  ];
}

/** "E02 07:05 — 3 jobs", at the time the block shows; `null` for a step that is not a stop. */
export function stopMenuHeader(task: Task, translate: Translate): ContextMenuHeader | null {
  const jobs = task.jobUids?.length ?? 0;
  if (task.kind !== 'work' || jobs === 0) return null;
  const count =
    jobs === 1
      ? translate(`${MENU}.jobs.one`)
      : translate(`${MENU}.jobs.other`, { count: jobs });
  return {
    entityType: translate(`${MENU}.stop`),
    name: `${task.name} ${format(task.startDate, 'HH:mm')} — ${count}`
  };
}

/** The entries a route's start marker adds in edit mode; the caller decides whether it is editable. */
export interface RouteStartMenuContext {
  row: TimelineRow;
  /** The start marker. */
  task: Task;
  /** The session's route starts, by route uid. */
  starts: Record<string, string>;
  dispatch: (gesture: PlanEditGesture) => void;
  openForm: (request: PlanEditFormRequest) => void;
  translate: Translate;
}

export function routeStartMenuEntries(ctx: RouteStartMenuContext): ContextMenuOption[] {
  const { row, task, translate } = ctx;
  const first = row.route && departureAction(row.route);
  if (!row.route || !first) return [];
  const routeUid = row.route.uid;
  const entries: ContextMenuOption[] = [
    {
      icon: ICONS.clock,
      label: translate(`${MENU}.set_start`),
      action: () =>
        ctx.openForm({
          kind: 'route-start',
          routeUid,
          routeName: row.name,
          current: actionStart(first).toISOString(),
          siteTimeZone:
            task.siteTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        })
    }
  ];
  if (ctx.starts[routeUid]) {
    entries.push({
      icon: `${ICONS.pushPin} icon-slashed`,
      label: translate(`${MENU}.release_start`),
      action: () => ctx.dispatch({ kind: 'set-route-start', routeUid, at: null })
    });
  }
  return entries;
}

/** "Route start" over "R-2041 05:30", at the time the marker shows. */
export function routeStartMenuHeader(
  row: TimelineRow,
  task: Task,
  translate: Translate
): ContextMenuHeader {
  return {
    entityType: translate(`${MENU}.route_start`),
    name: `${row.name} ${format(task.startDate, 'HH:mm')}`
  };
}

export function routeMenuHeader(row: TimelineRow, translate: Translate): ContextMenuHeader {
  return { entityType: translate(`${MENU}.route`), name: row.name };
}

/** What a route label's menu needs to offer session edits. */
export interface RouteMenuContext {
  row: TimelineRow;
  /** The shown routes, the targets of a swap. */
  rows: readonly TimelineRow[];
  dispatch: (gesture: PlanEditGesture) => void;
  openForm: (request: PlanEditFormRequest) => void;
  /** Opens the company picker; `onPick` gets the chosen company. */
  chooseCompany: (current: string | null, onPick: (companyUid: string) => void) => void;
  translate: Translate;
}

/**
 * Why DL would refuse to remove the route, as a menu hint key; `null` when it can go. DL keeps an
 * emptied route through the recompute, so a route left without stops still offers it.
 */
function removeRouteBlocker(route: UserRouteV1Model): string | null {
  return route.actions.some((action) => getHandledJobUids(action).length > 0)
    ? 'remove_route_has_stops'
    : null;
}

/**
 * The entries a route label's menu adds in edit mode. A validated route is frozen, and a route
 * still opening has no uid DL would know: neither gets any.
 */
export function routeMenuEntries(ctx: RouteMenuContext): ContextMenuOption[] {
  const { row, translate } = ctx;
  if (!row.route || row.validated || isRouteRef(row.uid)) return [];
  const routeUid = row.uid;
  const company = row.route.company_uid ?? null;
  const removeBlocker = removeRouteBlocker(row.route);

  const swapTargets: ContextMenuOption[] = ctx.rows
    .filter((other) => other.uid !== routeUid && other.route && !isRouteRef(other.uid))
    .map((other) => ({
      icon: ICONS.path,
      label: other.name,
      disabled: other.validated,
      hint: other.validated ? translate(`${MENU}.swap_validated`) : undefined,
      action: () => ctx.openForm({ kind: 'swap', routeUid, otherRouteUid: other.uid })
    }));
  swapTargets.push({
    icon: ICONS.magnifyingGlass,
    label: translate(`${MENU}.swap_other_route`),
    action: () => ctx.openForm({ kind: 'swap', routeUid, otherRouteUid: null })
  });

  return [
    {
      icon: ICONS.truck,
      label: translate(`${MENU}.change_resources`),
      action: () => ctx.openForm({ kind: 'resources', routeUid })
    },
    {
      icon: ICONS.factory,
      label: translate(`${MENU}.company`),
      hint: translate(`${MENU}.company_help`),
      action: () => {},
      subOptions: [
        {
          icon: ICONS.x,
          label: translate(`${MENU}.clear_company`),
          disabled: !company,
          action: () => ctx.dispatch({ kind: 'set-company', routeUid, companyUid: null })
        },
        {
          icon: ICONS.magnifyingGlass,
          label: translate(`${MENU}.choose_company`),
          action: () =>
            ctx.chooseCompany(company, (companyUid) =>
              ctx.dispatch({ kind: 'set-company', routeUid, companyUid })
            )
        }
      ]
    },
    {
      icon: ICONS.arrowsLeftRight,
      label: translate(`${MENU}.swap_resources`),
      action: () => {},
      subOptions: swapTargets
    },
    {
      icon: ICONS.lock,
      label: translate('visualization.timeline.context_menu.lock'),
      action: () => {},
      subOptions: ROUTE_LOCK_OPTIONS.map((option) => ({
        icon: option.icon,
        label: translate(option.labelKey),
        disabled: row.lockCfg === option.value,
        action: () => ctx.dispatch({ kind: 'set-lock', routeUid, lockCfg: option.value })
      }))
    },
    // Offered whether or not a start is known: DL keeps one through a resume, which this tab
    // forgets.
    {
      icon: `${ICONS.pushPin} icon-slashed`,
      label: translate(`${MENU}.release_start`),
      action: () => ctx.dispatch({ kind: 'set-route-start', routeUid, at: null })
    },
    {
      icon: ICONS.trash,
      label: translate(`${MENU}.remove_route`),
      disabled: removeBlocker !== null,
      hint: removeBlocker === null ? undefined : translate(`${MENU}.${removeBlocker}`),
      action: () => ctx.dispatch({ kind: 'remove-route', routeUid })
    }
  ];
}
